import React from "react";
import clsx from "clsx";
import { Icon } from "./Icon";

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "destructive" | "success" | "warning";
  className?: string;
  children: React.ReactNode;
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(({ className, variant = "default", children, ...props }, ref) => {
  const variants = {
    default: "border-slate-200 text-slate-900 dark:border-slate-700 dark:text-slate-100 bg-slate-50 dark:bg-slate-800",
    destructive: "border-error/20 text-error-dark dark:border-error/30 dark:text-error-light bg-error/10 dark:bg-error/20",
    success: "border-success/20 text-success-dark dark:border-success/30 dark:text-success-light bg-success/10 dark:bg-success/20",
    warning: "border-warning/20 text-warning-dark dark:border-warning/30 dark:text-warning-light bg-warning/10 dark:bg-warning/20",
  };

  return (
    <div
      ref={ref}
      role="alert"
      className={clsx("relative w-full rounded-lg border p-4", variants[variant], className)}
      {...props}
    >
      {children}
    </div>
  );
});

Alert.displayName = "Alert";

export const AlertTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={clsx("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
));

AlertTitle.displayName = "AlertTitle";

export const AlertDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={clsx("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
));

AlertDescription.displayName = "AlertDescription";
